import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { ITableConfig } from 'projects/common/src/app/interfaces/table/table-config';
import { ITableDataParams } from 'projects/common/src/app/interfaces/table/table-data-params';
import { ITableExtraParams } from 'projects/common/src/app/interfaces/table/table-extra-params';
import { ITableHeaderParams } from 'projects/common/src/app/interfaces/table/table-header-params';
import { LanguageService } from 'projects/common/src/app/services/parameters/language/language.service';

import { TableConfigService } from 'projects/common/src/app/services/table/table-config.service';
import { isOutOfParkConfigColor, isOutOfParkConfigTooltip, makeBadgeConfig } from 'projects/common/src/app/services/utils/utilities';
import { DatePresentationPipe } from 'src/app/modules/core/pipes/date-presentation.pipe';
import { IMachineStatus } from 'src/app/modules/widget/interfaces/machine-status';

@Component({
  selector: 'app-machine-status-table',
  templateUrl: './machine-status-table.component.html',
  styleUrls: ['./machine-status-table.component.scss'],
})
export class MachineStatusTableComponent implements OnInit {
  @Input()
  public collection: IMachineStatus[];

  @Input()
  public currentMachine: string;

  public tableConfig: ITableConfig;
  public tableData: any;

  constructor(
    private cdRef: ChangeDetectorRef,
    private tableService: TableConfigService,
    private datePipe: DatePresentationPipe,
    private _languageService: LanguageService,
  ) {}

  ngOnInit(): void {
    this.initSetTable();
  }

  public initSetTable(): void {
    this.tableData = [];
    for (const machine of this.collection) {
      this.tableData.push({
        machineName: machine.machineName,
        operatorName: machine.operatorName,
        statusName: machine.statusName,
        lastStatus: machine.lastStatus ? this.datePipe.transform(machine.lastStatus, 'hms') : '-',
        countMeasure: machine.countMeasure,
        lastEvent: machine.lastEvent ? this.datePipe.transform(machine.lastEvent, 'hms') : '-',
        lastZoneName: machine.lastZoneName,
        isOutOfPark: machine.isOutOfPark,
        statusColor: machine.statusColor,
        secondColor: machine.secondColor,
        thirdColor: machine.thirdColor,
      });
    }

    this.setTableMachineConfig(this.tableData);
  }

  public setTableMachineConfig(tableData: any[], noDataMsgParam?: string): void {
    // if there is no data, return
    if (!tableData || !tableData.length) return;

    const excludeColumns = [
      'companyId',
      'lastZoneId',
      'machineId',
      'machineModelId',
      'isConected',
      'machineModelName',
      'machineType',
      'machineTypeId',
      'operatorId',
      'statusFamilyColor',
      'statusFamilyName',
      'statusId',
      'statusImage',
      'statusType',
      'totalMeasure',
      'statusColor',
      'secondColor',
      'thirdColor',
      'isOutOfPark',
    ];

    const customLargeColumns = [
      { field: 'machineName', isLargeColumn: true, flexGrow: 0.4, customPadding: '6px 0px 3px 5px' },
      { field: 'operatorName', isLargeColumn: true, flexGrow: 1, customPadding: '6px 0px 3px 0px' },
      { field: 'statusName', isLargeColumn: true, flexGrow: 0.6, customPadding: '6px 0px 3px 20px' },
      { field: 'lastStatus', isLargeColumn: true, flexGrow: 0.8, customPadding: '6px 0px 3px 0px' },
      { field: 'countMeasure', isLargeColumn: true, flexGrow: 0.4, customPadding: '6px 0px 3px 40px' },
      { field: 'lastEvent', isLargeColumn: true, flexGrow: 0.8, customPadding: '6px 0px 0px 40px' },
      { field: 'lastZoneName', isLargeColumn: true, flexGrow: 0.8, customPadding: '6px 0px 3px 20px' },
    ];

    const countMeasureTraduction = this.getTraductionHeader(this.currentMachine);
    const replaceColumnName = [
      { prevName: 'operatorName', newName: 'operator' },
      { prevName: 'countMeasure', newName: countMeasureTraduction },
      { prevName: 'lastEvent', newName: 'last-production' },
      { prevName: 'lastZoneName', newName: 'last_ubication' },
      { prevName: 'statusName', newName: 'status' },
      { prevName: 'lastStatus', newName: 'since' },
    ];

    const deactivateTitle = [
      { field: 'operatorName' },
      { field: 'lastZoneName' },
      { field: 'statusName' },
      { field: 'lastStatus' },
      { field: 'lastEvent' },
      { field: 'countMeasure' },
    ];

    // get all the keys from the first object in the array to get the sortable columns
    const sortables = Object.keys(tableData[0]).filter((key) => !['edit'].includes(key));

    const headerParams: ITableHeaderParams = {
      data: tableData,
      customLargeColumns,
      excludeColumns,
      replaceColumnName,
      sortables,
    };
    const datasetParams: ITableDataParams = {
      tableData,
      isActive: true,
      excludeColumns,
      customLargeColumns,
      deactivateTitle,
      isActiveTooltip: false,
      badge: [{ field: 'statusName', badgeList: makeBadgeConfig(tableData) }],
      textColorConfig: isOutOfParkConfigColor(tableData, 'lastZoneName'),
      tooltipDynamicConfig: isOutOfParkConfigTooltip(tableData, 'lastZoneName', this._languageService),
    };

    const extraParams: ITableExtraParams = {
      noDataMsg: tableData.length === 0 ? 'm056' : noDataMsgParam ? '_m021' : 'm021',
      noDataMsgParam,
    };

    const header = this.tableService.setHeaderConfig(headerParams);
    const dataset = this.tableService.setDatasetConfig(datasetParams);

    // set the table config null to avoid the table to be rendered before the data is ready
    this.tableConfig = null;

    this.tableConfig = { header, dataset, extraParams };
    this.cdRef.detectChanges();
  }

  private getTraductionHeader(currentMachine: string): string {
    switch (currentMachine) {
      case 'lhd':
        return this._languageService.message('shelves');
      case 'jumbo':
        return this._languageService.message('drilling');
      case 'truck':
        return this._languageService.message('loops');
      default:
        return this._languageService.message('shelves');
    }
  }
}
